import { createContext, useContext, ReactNode } from 'react';

interface LoadingContextValue {
  loading: boolean;
}

const LoadingContext = createContext<LoadingContextValue>({ loading: false });

export interface LoadingProviderProps {
  active: boolean;
  children: ReactNode;
}

/**
 * LoadingProvider - Propagates loading state down to skeleton-aware primitives
 *
 * Wrap content in this provider to enable automatic skeleton rendering
 * for child primitives (Text, Heading, Code, Badge, etc.) when loading.
 *
 * @example
 * ```tsx
 * <Loading active={!data}>
 *   <Card>
 *     <Heading width="10rem">{data?.title}</Heading>
 *     <Text width="20rem">{data?.description}</Text>
 *   </Card>
 * </Loading>
 * ```
 */
export function Loading({ active, children }: LoadingProviderProps) {
  return (
    <LoadingContext.Provider value={{ loading: active }}>
      {children}
    </LoadingContext.Provider>
  );
}

/**
 * Hook to check if currently in a loading context
 */
export function useLoading(): boolean {
  const context = useContext(LoadingContext);
  return context.loading;
}
